import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import {
  Typography,
  Grid,
  Paper,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Box,
  Snackbar,
} from "@mui/material";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import PropTypes from "prop-types";
import "./DriverDashboard.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { styled } from "@mui/material/styles";
import moment from "moment-timezone";
import { FaInfoCircle, FaBed } from "react-icons/fa"; // Import icons

// Register Chart.js components
Chart.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
      style={{ width: "100%" }}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}
TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const CustomTabs = styled(Tabs)({
  "& .MuiTabs-indicator": {
    background: "linear-gradient(90deg, #10b981 0%, #f59e42 100%)",
    height: 4,
    borderRadius: 2,
  },
});
const CustomTab = styled(Tab)(({ theme }) => ({
  color: "#64748b",
  fontWeight: 700,
  fontSize: 16,
  "&.Mui-selected": {
    color: "#10b981",
    background: "linear-gradient(90deg, #f0fdfa 0%, #fef9c3 100%)",
    borderRadius: 8,
  },
}));

const DriverDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    drivers: [],
    stats: {
      totalDrivers: 0,
      activeDrivers: 0,
      assignedDrivers: 0,
      driversNeedingRest: 0,
      approachingLimit: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [restDialogOpen, setRestDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [restForm, setRestForm] = useState({
    startDate: dayjs(),
    durationHours: 24,
    notes: "",
  });
  const [tabIndex, setTabIndex] = useState(0);
  const [filterOpen, setFilterOpen] = useState({});
  const [searchValue, setSearchValue] = useState({});
  const [alert, setAlert] = useState({
    open: false,
    severity: "info",
    message: "",
  });
  const [showTripWarning, setShowTripWarning] = useState(false);
  const navigate = useNavigate();

  const handleSearchChange = (field, value, params) => {
    setSearchValue((prev) => ({ ...prev, [field]: value }));

    if (params && params.api) {
      params.api.setQuickFilter("");
    }
  };

  const handleFilterIconClick = (field) => {
    setFilterOpen((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/api/driver-dashboard`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const drivers = response.data.drivers || [];
      const stats = {
        totalDrivers: drivers.length,
        activeDrivers: drivers.filter((d) => d.is_active === "Y").length,
        assignedDrivers: drivers.filter((d) => d.assigned_trips > 0).length,
        driversNeedingRest: drivers.filter((d) => d.needs_rest).length,
        approachingLimit: drivers.filter(
          (d) => d.consecutive_work_days >= 5 && !d.needs_rest
        ).length,
      };

      setDashboardData({ drivers, stats });
    } catch (err) {
      console.error("Dashboard error:", err);
      setError(err.response?.data?.error || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 300000);
    return () => clearInterval(interval);
  }, []);

  const handleRegisterRest = (driver) => {
    setSelectedDriver(driver);
    setRestForm({
      startDate: dayjs(),
      durationHours: 24,
      notes: "",
    });
    setRestDialogOpen(true);
  };

  // const submitRestPeriod = async () => {
  //   try {
  //     const token = localStorage.getItem("token");

  //     // Format start date in UTC
  //     const startDateUTC = moment(restForm.startDate)
  //       .tz("Asia/Muscat")
  //       .utc()
  //       .format();

  //     // Calculate end date based on duration
  //     const endDateUTC = moment(restForm.startDate)
  //       .add(restForm.durationHours, "hours")
  //       .tz("Asia/Muscat")
  //       .utc()
  //       .format();

  //     await axios.post(
  //       `${process.env.REACT_APP_BASE_API_URL}/api/register-rest`,
  //       {
  //         driverName: selectedDriver.driver_name,
  //         startDate: startDateUTC,
  //         endDate: endDateUTC,
  //         notes: restForm.notes || "",
  //         driverId: selectedDriver.driver_id,
  //       },
  //       { headers: { Authorization: `Bearer ${token}` } }
  //     );

  //     // Show success notification
  //     setAlert({
  //       open: true,
  //       severity: "success",
  //       message: "Rest period registered successfully!",
  //     });

  //     setRestDialogOpen(false);
  //     fetchDashboardData();
  //   } catch (err) {
  //     console.error("Rest registration error:", err);

  //     // Show specific error if available
  //     const errorMsg =
  //       err.response?.data?.error || "Failed to register rest period";

  //     setAlert({
  //       open: true,
  //       severity: "error",
  //       message: errorMsg,
  //     });

  //     // If error mentions active trips, offer to view them
  //     if (errorMsg.includes("active trips")) {
  //       setShowTripWarning(true);
  //     }
  //   }
  // };
  const submitRestPeriod = async () => {
    try {
      const token = localStorage.getItem("token");

      // Format dates in UTC
      const startDateUTC = moment(restForm.startDate)
        .tz("Asia/Muscat")
        .utc()
        .format();

      const endDateUTC = moment(restForm.startDate)
        .add(restForm.durationHours, "hours")
        .tz("Asia/Muscat")
        .utc()
        .format();

      await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/api/register-rest`,
        {
          driverName: selectedDriver.driver_name,
          startDate: startDateUTC,
          endDate: endDateUTC,
          notes: restForm.notes || "",
          driverId: selectedDriver.driver_id,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAlert({
        open: true,
        severity: "success",
        message: "Rest period registered successfully!",
      });
      setRestDialogOpen(false);
      fetchDashboardData();
    } catch (err) {
      console.error("Rest registration error:", err);
      setAlert({
        open: true,
        severity: "error",
        message: err.response?.data?.error || "Failed to register rest period",
      });
    }
  };

  const filteredDrivers = dashboardData.drivers.filter((driver) => {
    if (filter === "needsRest") return driver.needs_rest;
    if (filter === "active")
      return !driver.needs_rest && driver.is_active === "Y";
    if (filter === "inactive") return driver.is_active !== "Y";
    if (filter === "approaching")
      return driver.consecutive_work_days >= 5 && !driver.needs_rest;
    if (filter === "assigned") return driver.assigned_trips > 0;
    return true;
  });

  // Chart data configurations
  const statusData = {
    labels: ["Active", "Approaching Limit", "Needs Rest", "Inactive"],
    datasets: [
      {
        data: [
          dashboardData.stats.activeDrivers -
            dashboardData.stats.approachingLimit -
            dashboardData.stats.driversNeedingRest,
          dashboardData.stats.approachingLimit,
          dashboardData.stats.driversNeedingRest,
          dashboardData.stats.totalDrivers - dashboardData.stats.activeDrivers,
        ],
        backgroundColor: [
          "rgba(75, 192, 192, 0.7)",
          "rgba(255, 206, 86, 0.7)",
          "rgba(255, 99, 132, 0.7)",
          "rgba(153, 102, 255, 0.7)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const workDaysData = {
    labels: ["0-2 Days", "3-4 Days", "5 Days", "6+ Days"],
    datasets: [
      {
        label: "Drivers by Work Days",
        data: [
          dashboardData.drivers.filter((d) => d.consecutive_work_days <= 2)
            .length,
          dashboardData.drivers.filter(
            (d) => d.consecutive_work_days >= 3 && d.consecutive_work_days <= 4
          ).length,
          dashboardData.drivers.filter((d) => d.consecutive_work_days === 5)
            .length,
          dashboardData.drivers.filter((d) => d.consecutive_work_days >= 6)
            .length,
        ],
        backgroundColor: "rgba(54, 162, 235, 0.7)",
        borderWidth: 1,
      },
    ],
  };

  // Column definitions for AG-Grid
  const columnDefs = [
    {
      field: "driver_name",
      headerName: "Driver Name",
      width: 240,
      filter: "agTextColumnFilter",
      floatingFilter: false,
      headerComponentFramework: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
          }}
        >
          <span>Driver Name</span>
          <span
            style={{ marginLeft: 6, cursor: "pointer" }}
            onClick={() => handleFilterIconClick("driver_name")}
          >
            <FontAwesomeIcon icon={faFilter} />
          </span>
          {filterOpen.driver_name && (
            <div
              style={{
                position: "absolute",
                top: 30,
                left: 0,
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                borderRadius: 8,
                padding: 8,
                zIndex: 10,
                minWidth: 180,
              }}
            >
              <input
                type="text"
                placeholder="Search..."
                value={searchValue.driver_name || ""}
                onChange={(e) =>
                  handleSearchChange("driver_name", e.target.value, params)
                }
                style={{
                  width: "100%",
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid #e0e7ef",
                  fontSize: 13,
                }}
              />
            </div>
          )}
        </div>
      ),
      cellRenderer: (params) => {
        const value =
          typeof params.value === "string"
            ? params.value
            : params.data?.driver_name || "";
        return (
          <span
            style={{
              fontWeight: params.data?.needs_rest ? "bold" : "normal",
              color: params.data?.needs_rest ? "#d32f2f" : "inherit",
            }}
          >
            {value}
            {params.data?.needs_rest ? " ⚠️" : ""}
          </span>
        );
      },
    },
    {
      field: "reporting_driver_code",
      headerName: "Driver Code",
      width: 120,
      filter: "agTextColumnFilter",
      floatingFilter: false,
      headerComponentFramework: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
          }}
        >
          <span>Driver Code</span>
          <span
            style={{ marginLeft: 6, cursor: "pointer" }}
            onClick={() => handleFilterIconClick("reporting_driver_code")}
          >
            <FontAwesomeIcon icon={faFilter} />
          </span>
          {filterOpen.reporting_driver_code && (
            <div
              style={{
                position: "absolute",
                top: 30,
                left: 0,
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                borderRadius: 8,
                padding: 8,
                zIndex: 10,
                minWidth: 140,
              }}
            >
              <input
                type="text"
                placeholder="Search..."
                value={searchValue.reporting_driver_code || ""}
                onChange={(e) =>
                  handleSearchChange(
                    "reporting_driver_code",
                    e.target.value,
                    params
                  )
                }
                style={{
                  width: "100%",
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid #e0e7ef",
                  fontSize: 13,
                }}
              />
            </div>
          )}
        </div>
      ),
    },
    {
      field: "rest_status",
      headerName: "Status",
      width: 100,
      filter: "agTextColumnFilter",
      floatingFilter: false,
      headerComponentFramework: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
          }}
        >
          <span>Status</span>
          <span
            style={{ marginLeft: 6, cursor: "pointer" }}
            onClick={() => handleFilterIconClick("rest_status")}
          >
            <FontAwesomeIcon icon={faFilter} />
          </span>
          {filterOpen.rest_status && (
            <div
              style={{
                position: "absolute",
                top: 30,
                left: 0,
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                borderRadius: 8,
                padding: 8,
                zIndex: 10,
                minWidth: 120,
              }}
            >
              <input
                type="text"
                placeholder="Search..."
                value={searchValue.rest_status || ""}
                onChange={(e) =>
                  handleSearchChange("rest_status", e.target.value, params)
                }
                style={{
                  width: "100%",
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid #e0e7ef",
                  fontSize: 13,
                }}
              />
            </div>
          )}
        </div>
      ),
      cellStyle: (params) => {
        if (params.value === "REQUIRES REST")
          return { backgroundColor: "rgba(255, 99, 132, 0.2)" };
        if (params.value === "APPROACHING LIMIT")
          return { backgroundColor: "rgba(255, 206, 86, 0.2)" };
        return null;
      },
    },
    {
      field: "consecutive_work_days",
      headerName: "Work Days",
      width: 120,
      filter: "agNumberColumnFilter",
      floatingFilter: false,
      headerComponentFramework: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
          }}
        >
          <span>Work Days</span>
          <span
            style={{ marginLeft: 6, cursor: "pointer" }}
            onClick={() => handleFilterIconClick("consecutive_work_days")}
          >
            <FontAwesomeIcon icon={faFilter} />
          </span>
          {filterOpen.consecutive_work_days && (
            <div
              style={{
                position: "absolute",
                top: 30,
                left: 0,
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                borderRadius: 8,
                padding: 8,
                zIndex: 10,
                minWidth: 100,
              }}
            >
              <input
                type="number"
                placeholder="Search..."
                value={searchValue.consecutive_work_days || ""}
                onChange={(e) =>
                  handleSearchChange(
                    "consecutive_work_days",
                    e.target.value,
                    params
                  )
                }
                style={{
                  width: "100%",
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid #e0e7ef",
                  fontSize: 13,
                }}
              />
            </div>
          )}
        </div>
      ),
      cellRenderer: (params) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: "8px" }}>{params.value}</span>
          <div
            style={{
              height: "8px",
              width: "50px",
              backgroundColor: "#e0e0e0",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${Math.min(100, (params.value / 6) * 100)}%`,
                backgroundColor:
                  params.value >= 6
                    ? "#f44336"
                    : params.value >= 5
                    ? "#ffc107"
                    : "#4caf50",
                transition: "width 0.3s",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "active_journeys",
      headerName: "Active Trips",
      width: 120,
      filter: "agNumberColumnFilter",
      floatingFilter: false,
      headerComponentFramework: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
          }}
        >
          <span>Active Trips</span>
          <span
            style={{ marginLeft: 6, cursor: "pointer" }}
            onClick={() => handleFilterIconClick("active_journeys")}
          >
            <FontAwesomeIcon icon={faFilter} />
          </span>
          {filterOpen.active_journeys && (
            <div
              style={{
                position: "absolute",
                top: 30,
                left: 0,
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                borderRadius: 8,
                padding: 8,
                zIndex: 10,
                minWidth: 100,
              }}
            >
              <input
                type="number"
                placeholder="Search..."
                value={searchValue.active_journeys || ""}
                onChange={(e) =>
                  handleSearchChange("active_journeys", e.target.value, params)
                }
                style={{
                  width: "100%",
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid #e0e7ef",
                  fontSize: 13,
                }}
              />
            </div>
          )}
        </div>
      ),
    },
    {
      field: "assigned_trips",
      headerName: "Assigned Trips",
      width: 120,
      filter: "agNumberColumnFilter",
      floatingFilter: false,
      headerComponentFramework: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
          }}
        >
          <span>Assigned Trips</span>
          <span
            style={{ marginLeft: 6, cursor: "pointer" }}
            onClick={() => handleFilterIconClick("assigned_trips")}
          >
            <FontAwesomeIcon icon={faFilter} />
          </span>
          {filterOpen.assigned_trips && (
            <div
              style={{
                position: "absolute",
                top: 30,
                left: 0,
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                borderRadius: 8,
                padding: 8,
                zIndex: 10,
                minWidth: 100,
              }}
            >
              <input
                type="number"
                placeholder="Search..."
                value={searchValue.assigned_trips || ""}
                onChange={(e) =>
                  handleSearchChange("assigned_trips", e.target.value, params)
                }
                style={{
                  width: "100%",
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid #e0e7ef",
                  fontSize: 13,
                }}
              />
            </div>
          )}
        </div>
      ),
    },
    {
      field: "last_work_date",
      headerName: "Last Work",
      width: 120,
      filter: "agDateColumnFilter",
      floatingFilter: false,
      headerComponentFramework: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
          }}
        >
          <span>Last Work</span>
          <span
            style={{ marginLeft: 6, cursor: "pointer" }}
            onClick={() => handleFilterIconClick("last_work_date")}
          >
            <FontAwesomeIcon icon={faFilter} />
          </span>
          {filterOpen.last_work_date && (
            <div
              style={{
                position: "absolute",
                top: 30,
                left: 0,
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                borderRadius: 8,
                padding: 8,
                zIndex: 10,
                minWidth: 120,
              }}
            >
              <input
                type="text"
                placeholder="YYYY-MM-DD"
                value={searchValue.last_work_date || ""}
                onChange={(e) =>
                  handleSearchChange("last_work_date", e.target.value, params)
                }
                style={{
                  width: "100%",
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid #e0e7ef",
                  fontSize: 13,
                }}
              />
            </div>
          )}
        </div>
      ),
      valueFormatter: (params) =>
        params.value
          ? moment(params.value).tz("Asia/Muscat").format("DD/MM/YYYY")
          : "Never",
    },
    {
      field: "last_rest_date",
      headerName: "Last Rest",
      width: 160,
      filter: "agDateColumnFilter",
      floatingFilter: false,
      headerComponentFramework: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
          }}
        >
          <span>Last Rest</span>
          <span
            style={{ marginLeft: 6, cursor: "pointer" }}
            onClick={() => handleFilterIconClick("last_rest_date")}
          >
            <FontAwesomeIcon icon={faFilter} />
          </span>
          {filterOpen.last_rest_date && (
            <div
              style={{
                position: "absolute",
                top: 30,
                left: 0,
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                borderRadius: 8,
                padding: 8,
                zIndex: 10,
                minWidth: 120,
              }}
            >
              <input
                type="text"
                placeholder="YYYY-MM-DD"
                value={searchValue.last_rest_date || ""}
                onChange={(e) =>
                  handleSearchChange("last_rest_date", e.target.value, params)
                }
                style={{
                  width: "100%",
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid #e0e7ef",
                  fontSize: 13,
                }}
              />
            </div>
          )}
        </div>
      ),
      valueFormatter: (params) => formatRestDate(params.value),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140, // Adjust width to accommodate larger icons
      filter: false,
      floatingFilter: false,
      cellRenderer: (params) => {
        if (!params.data) return null;

        return (
          <div
            style={{ display: "flex", gap: "12px", justifyContent: "center" }}
          >
            <FaInfoCircle
              title="Details"
              onClick={() =>
                navigate(`/driver-details/${params.data.driver_id}`)
              }
              style={{
                color: "#1976d2",
                fontSize: "24px", // Increased size
                cursor: "pointer",
                transition: "color 0.3s",
              }}
              onMouseEnter={(e) => (e.target.style.color = "#1565c0")}
              onMouseLeave={(e) => (e.target.style.color = "#1976d2")}
            />

            {(params.data.rest_status === "REQUIRES REST" ||
              params.data.rest_status === "APPROACHING LIMIT") && (
              <FaBed
                title="Register Rest"
                onClick={() => handleRegisterRest(params.data)}
                style={{
                  color:
                    params.data.rest_status === "REQUIRES REST"
                      ? "#f44336"
                      : "#ff9800",
                  fontSize: "24px", // Increased size
                  cursor: "pointer",
                  transition: "color 0.3s",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.color =
                    params.data.rest_status === "REQUIRES REST"
                      ? "#d32f2f"
                      : "#f57c00")
                }
                onMouseLeave={(e) =>
                  (e.target.style.color =
                    params.data.rest_status === "REQUIRES REST"
                      ? "#f44336"
                      : "#ff9800")
                }
              />
            )}
          </div>
        );
      },
    },
  ];

  const formatRestDate = (dateStr) => {
    if (
      !dateStr ||
      dateStr.startsWith("1900-01-01") ||
      dateStr === "1900-01-01"
    ) {
      return "No rest recorded";
    }
    return moment(dateStr).tz("Asia/Muscat").format("DD/MM/YYYY");
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <CircularProgress />
        <Typography variant="body1" style={{ marginLeft: "16px" }}>
          Loading dashboard data...
        </Typography>
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" style={{ margin: "20px" }}>
        {error}
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div style={{ padding: "16px" }}>
        {/* Back Arrow to Main Page */}
        <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
          <Button
            variant="text"
            onClick={() => navigate("/main")}
            sx={{
              minWidth: 0,
              mr: 1,
              fontSize: 14,
              color: "#1976d2",
              fontWeight: 700,
              textTransform: "none",
              p: 0,
            }}
            startIcon={
              <span
                style={{
                  display: "inline-block",
                  fontSize: 14,
                  marginRight: 4,
                  verticalAlign: "middle",
                }}
              >
                &#8592;
              </span>
            }
          >
            Main Page
          </Button>
        </Box>

        {/* Notification Snackbar */}
        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={() => setAlert({ ...alert, open: false })}
        >
          <Alert
            severity={alert.severity}
            onClose={() => setAlert({ ...alert, open: false })}
          >
            {alert.message}
          </Alert>
        </Snackbar>

        {/* Active Trips Warning Dialog */}
        {showTripWarning && (
          <Dialog
            open={showTripWarning}
            onClose={() => setShowTripWarning(false)}
          >
            <DialogTitle>Active Trips Found</DialogTitle>
            <DialogContent>
              <Typography>
                This driver has active trips that must be completed before rest
                can be registered.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowTripWarning(false)}>Close</Button>
              <Button
                onClick={() => {
                  navigate(`/driver-trips/${selectedDriver.driver_id}`);
                  setShowTripWarning(false);
                }}
                color="primary"
              >
                View Trips
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Summary Cards */}
        <div className="dashboard-stats">
          <div
            className="stat-card total"
            style={{ cursor: "pointer", opacity: filter === "all" ? 1 : 0.85 }}
            onClick={() => setFilter("all")}
          >
            <h3>Total Drivers</h3>
            <p>{dashboardData.stats.totalDrivers}</p>
          </div>
          <div
            className="stat-card active"
            style={{
              cursor: "pointer",
              opacity: filter === "active" ? 1 : 0.85,
            }}
            onClick={() => setFilter("active")}
          >
            <h3>Active Drivers</h3>
            <p>{dashboardData.stats.activeDrivers}</p>
          </div>
          <div
            className="stat-card"
            style={{
              background: "linear-gradient(135deg, #f0f9ff 0%, #a5b4fc 100%)",
              borderLeft: "3px solid #6366f1",
              cursor: "pointer",
              opacity: filter === "assigned" ? 1 : 0.85,
            }}
            onClick={() => setFilter("assigned")}
          >
            <h3>Assigned to Trips</h3>
            <p>{dashboardData.stats.assignedDrivers}</p>
          </div>
          <div
            className="stat-card"
            style={{
              background: "linear-gradient(135deg, #fef9c3 0%, #fde68a 100%)",
              borderLeft: "3px solid #f59e42",
              cursor: "pointer",
              opacity: filter === "approaching" ? 1 : 0.85,
            }}
            onClick={() => setFilter("approaching")}
          >
            <h3>Approaching Limit</h3>
            <p>{dashboardData.stats.approachingLimit}</p>
          </div>
          <div
            className="stat-card resting"
            style={{
              cursor: "pointer",
              opacity: filter === "needsRest" ? 1 : 0.85,
            }}
            onClick={() => setFilter("needsRest")}
          >
            <h3>Need Rest</h3>
            <p>{dashboardData.stats.driversNeedingRest}</p>
          </div>
          <div
            className="stat-card"
            style={{
              background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
              borderLeft: "3px solid #64748b",
              cursor: "pointer",
              opacity: filter === "inactive" ? 1 : 0.85,
            }}
            onClick={() => setFilter("inactive")}
          >
            <h3>Inactive</h3>
            <p>
              {dashboardData.stats.totalDrivers -
                dashboardData.stats.activeDrivers}
            </p>
          </div>
        </div>

        {/* Tabbed Interface */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 2 }}>
          <CustomTabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            aria-label="dashboard tabs"
            variant="fullWidth"
          >
            <CustomTab label="Driver List" />
            <CustomTab label="Charts" />
          </CustomTabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={tabIndex} index={0}>
          {/* Drivers Table */}
          <Divider style={{ margin: "5px 0" }} />
          <Typography variant="h7" gutterBottom>
            Driver Details ({filteredDrivers.length} drivers)
          </Typography>
          <div
            className="ag-theme-material driver-dashboard-table"
            style={{
              width: "100%",
              marginTop: "10px",
              height: 540,
              overflow: "auto",
              borderRadius: 10,
              background: "#fff",
            }}
          >
            <AgGridReact
              columnDefs={columnDefs}
              rowData={filteredDrivers}
              pagination={true}
              paginationPageSize={20}
              suppressCellFocus={true}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: false,
                floatingFilter: false,
                flex: 1,
                minWidth: 100,
              }}
              onGridReady={(params) => params.api.sizeColumnsToFit()}
            />
          </div>
        </TabPanel>
        <TabPanel value={tabIndex} index={1}>
          {/* Charts */}
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                style={{
                  padding: "24px",
                  maxWidth: 580,
                  margin: "24px auto",
                  height: 400,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  boxSizing: "border-box",
                  borderRadius: 18,
                  background:
                    "linear-gradient(135deg, #f8fafc 0%, #e0f7fa 100%)",
                }}
              >
                <Typography variant="h7" gutterBottom>
                  Driver Status Distribution
                </Typography>
                <Doughnut
                  data={statusData}
                  height={400}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: "right" },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            const total = context.dataset.data.reduce(
                              (a, b) => a + b,
                              0
                            );
                            const value = context.raw;
                            const percentage = Math.round(
                              (value / total) * 100
                            );
                            return `${context.label}: ${value} (${percentage}%)`;
                          },
                        },
                      },
                    },
                  }}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                style={{
                  padding: "24px",
                  maxWidth: 580,
                  margin: "24px auto",
                  height: 400,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  boxSizing: "border-box",
                  borderRadius: 18,
                  background:
                    "linear-gradient(135deg, #f8fafc 0%, #e3f2fd 100%)",
                }}
              >
                <Typography variant="h7" gutterBottom>
                  Work Day Distribution
                </Typography>
                <Bar
                  data={workDaysData}
                  height={400}
                  options={{
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          precision: 0,
                        },
                      },
                    },
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                  }}
                />
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Rest Registration Dialog */}
        <Dialog
          open={restDialogOpen}
          onClose={() => setRestDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Register Rest Period for {selectedDriver?.driver_name}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} style={{ marginTop: "2px" }}>
              {/* <Grid item xs={12} style={{ marginBottom: "2px" }}>
                <Alert
                  severity={
                    selectedDriver?.rest_status === "REQUIRES REST"
                      ? "error"
                      : "warning"
                  }
                  style={{ padding: "8px", fontSize: "14px" }} // Reduced padding and font size
                >
                  {selectedDriver?.rest_status === "REQUIRES REST"
                    ? "Driver requires immediate rest"
                    : "Driver is approaching work limit"}
                </Alert>
              </Grid> */}

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Start Date"
                  value={restForm.startDate}
                  onChange={(newValue) =>
                    setRestForm({ ...restForm, startDate: newValue })
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  format="DD/MM/YYYY HH:mm"
                  ampm={false}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Duration (hours)"
                  value={restForm.durationHours}
                  onChange={(e) =>
                    setRestForm({ ...restForm, durationHours: e.target.value })
                  }
                  fullWidth
                >
                  <MenuItem value={24}>24 hours (1 day)</MenuItem>
                  <MenuItem value={48}>48 hours (2 days)</MenuItem>
                  <MenuItem value={72}>72 hours (3 days)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes (Optional)"
                  multiline
                  rows={3}
                  value={restForm.notes}
                  onChange={(e) =>
                    setRestForm({ ...restForm, notes: e.target.value })
                  }
                  fullWidth
                />
              </Grid>
              {selectedDriver && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Current Status:</strong>{" "}
                    <span
                      style={{
                        color:
                          selectedDriver.rest_status === "REQUIRES REST"
                            ? "#f44336"
                            : "#ff9800",
                      }}
                    >
                      {selectedDriver.rest_status}
                    </span>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Consecutive Work Days:</strong>{" "}
                    {selectedDriver.consecutive_work_days}/6 days
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Last Rest:</strong>{" "}
                    {formatRestDate(selectedDriver?.last_rest_date)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRestDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={submitRestPeriod}
              variant="contained"
              color={
                selectedDriver?.rest_status === "REQUIRES REST"
                  ? "error"
                  : "warning"
              }
            >
              Register Rest Period
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </LocalizationProvider>
  );
};
export default DriverDashboard;
